/**
 * Copilot tool registry — every write the copilot (and human-via-editor)
 * can perform is defined here as a typed tool with a `validate` step
 * (runs before any state mutation) and an `apply` step (returns
 * `undoData` shaped to reverse the action).
 *
 * Same validation rules the UI enforces at render time
 * (`DashboardRenderer.tsx InvalidBindingCard guard`), now run *before*
 * the proposal card is even rendered — so a bad LLM proposal shows up
 * red with the reason, Accept disabled.
 *
 * Phase 4 maps each tool to a Claude tool-call definition; the
 * validators stay identical, becoming the server-side guard inside the
 * ingester / dashboard API.
 */

import type {
  DataSource,
  WidgetInstance,
  WidgetLayout,
} from "@/lib/dashboard/types";
import type {
  DashboardsState,
} from "@/lib/dashboard/store";
import type { AlarmRule, AlarmsState } from "@/lib/alarms/store";
import type { ComputedTagsState } from "@/lib/compute/store";
import { WIDGET_REGISTRY } from "@/lib/dashboard/widget-registry";
import { isEditable } from "@/lib/dashboard/store";
import type { DataKind } from "@foundry/foundry-plant";

// ────────────────────────────────────────────────────────────────────
// Stores aggregate — what every tool's apply / validate has access to
// ────────────────────────────────────────────────────────────────────

export interface ToolStores {
  dashboards: DashboardsState;
  alarms: AlarmsState;
  computedTags: ComputedTagsState;
}

// ────────────────────────────────────────────────────────────────────
// Result shapes
// ────────────────────────────────────────────────────────────────────

export interface ValidationResult {
  ok: boolean;
  /** Human-readable reason — surfaced in the proposal card when ok=false. */
  reason?: string;
  /**
   * Optional suggestion the copilot can offer as a follow-up. e.g. if a
   * delete on a pack dashboard fails, suggest `clone_dashboard` instead.
   */
  suggestion?: { toolId: string; payload: unknown; rationale: string };
}

export interface ApplyResult {
  ok: boolean;
  reason?: string;
  /** Opaque blob the tool's `apply` later consumes to reverse the action. */
  undoData?: unknown;
  /** Short human-readable summary for the audit log + toast. */
  summary?: string;
}

export type ToolRisk = "low" | "medium" | "destructive";

export interface CopilotTool<Payload = unknown, UndoData = unknown> {
  id: string;
  description: string;
  risk: ToolRisk;
  validate: (payload: Payload, stores: ToolStores) => ValidationResult;
  apply: (
    payload: Payload,
    stores: ToolStores,
  ) => ApplyResult & { undoData?: UndoData };
}

// ────────────────────────────────────────────────────────────────────
// Tool payloads — each is a discriminated union member of ProposalApply
// ────────────────────────────────────────────────────────────────────

export interface CreateDashboardPayload {
  type: "create_dashboard";
  name: string;
  description?: string;
  initialWidgets?: WidgetInstance[];
}

export interface CloneDashboardPayload {
  type: "clone_dashboard";
  sourceId: string;
  name?: string;
}

export interface AddWidgetPayload {
  type: "add_widget";
  dashboardId: string;
  widget: {
    id?: string;
    type: WidgetInstance["type"];
    title?: string;
    subtitle?: string;
    layout?: WidgetLayout;
    dataSource?: DataSource;
    config?: Record<string, unknown>;
  };
}

export interface BindWidgetSlotPayload {
  type: "bind_widget_slot";
  dashboardId: string;
  widgetId: string;
  slotId: string;
  keys: string[];
  /** Required because the alias is part of DataSource — the picker would set this. */
  alias: string;
  /** "tag" | "port" — defaults to "tag" for copilot-authored bindings. */
  kind?: "tag" | "port";
}

export interface SetWidgetTitlePayload {
  type: "set_widget_title";
  dashboardId: string;
  widgetId: string;
  title: string;
  subtitle?: string;
}

export interface DeleteWidgetPayload {
  type: "delete_widget";
  dashboardId: string;
  widgetId: string;
}

export interface CreateAlarmRulePayload {
  type: "create_alarm_rule";
  name: string;
  targetTagId: string;
  targetLabel?: string;
  condition: AlarmRule["condition"];
  severity?: AlarmRule["severity"];
  channels?: string[];
  enabled?: boolean;
}

export interface UpdateAlarmRulePayload {
  type: "update_alarm_rule";
  id: string;
  patch: Partial<AlarmRule>;
}

export interface DeleteAlarmRulePayload {
  type: "delete_alarm_rule";
  id: string;
}

export interface PatchTagPayload {
  type: "patch_tag";
  /** Tag id in the catalog (FX_CATALOG_TAGS or — Phase 1 — /v1/iot/tags) */
  id: string;
  patch: {
    description?: string;
    unit?: string;
    min_value?: number;
    max_value?: number;
    data_kind?: DataKind;
    semantic_tag?: string;
  };
}

export type CopilotToolPayload =
  | CreateDashboardPayload
  | CloneDashboardPayload
  | AddWidgetPayload
  | BindWidgetSlotPayload
  | SetWidgetTitlePayload
  | DeleteWidgetPayload
  | CreateAlarmRulePayload
  | UpdateAlarmRulePayload
  | DeleteAlarmRulePayload
  | PatchTagPayload;

// ────────────────────────────────────────────────────────────────────
// Tool registry — 10 tools
// ────────────────────────────────────────────────────────────────────

// `as CopilotTool<P, U>` is used instead of the angle-bracket form because
// `erasableSyntaxOnly` (TS Node type-stripping support) forbids
// `<Type>expr` — it conflicts with JSX. `as` works fine and preserves
// the per-tool type annotation.

export const COPILOT_TOOLS = {
  create_dashboard: {
    id: "create_dashboard",
    description: "Create a new user-owned dashboard with an optional starter widget set.",
    risk: "low",
    validate: (p) => {
      if (!p.name?.trim()) return { ok: false, reason: "Dashboard name is required." };
      return { ok: true };
    },
    apply: (p, { dashboards }) => {
      const id = dashboards.create({
        name: p.name.trim(),
        description: p.description,
        widgets: p.initialWidgets ?? [],
      });
      return {
        ok: true,
        undoData: { dashboardId: id },
        summary: `Created dashboard "${p.name}"`,
      };
    },
  } as CopilotTool<CreateDashboardPayload, { dashboardId: string }>,

  clone_dashboard: {
    id: "clone_dashboard",
    description: "Clone an existing dashboard into a new editable copy.",
    risk: "low",
    validate: (p, { dashboards }) => {
      const src = dashboards.byId(p.sourceId);
      if (!src) return { ok: false, reason: `Dashboard "${p.sourceId}" not found.` };
      return { ok: true };
    },
    apply: (p, { dashboards }) => {
      const id = dashboards.clone(p.sourceId, p.name);
      const src = dashboards.byId(p.sourceId);
      return {
        ok: true,
        undoData: { dashboardId: id },
        summary: `Cloned "${src?.name ?? p.sourceId}" → "${p.name ?? `${src?.name} (copy)`}"`,
      };
    },
  } as CopilotTool<CloneDashboardPayload, { dashboardId: string }>,

  add_widget: {
    id: "add_widget",
    description: "Add a widget to an existing user-owned dashboard.",
    risk: "low",
    validate: (p, { dashboards }) => {
      const d = dashboards.byId(p.dashboardId);
      if (!d) return { ok: false, reason: `Dashboard "${p.dashboardId}" not found.` };
      if (!isEditable(d)) {
        return {
          ok: false,
          reason: `"${d.name}" is a pack-shipped dashboard — clone it first to edit.`,
          suggestion: {
            toolId: "clone_dashboard",
            payload: { type: "clone_dashboard", sourceId: p.dashboardId } as CloneDashboardPayload,
            rationale: "Clone the dashboard, then add the widget to the copy.",
          },
        };
      }
      const entry = WIDGET_REGISTRY[p.widget.type];
      if (!entry) return { ok: false, reason: `Unknown widget type "${p.widget.type}".` };
      return { ok: true };
    },
    apply: (p, { dashboards }) => {
      const entry = WIDGET_REGISTRY[p.widget.type];
      const id =
        p.widget.id ??
        `w-${p.widget.type}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      const d = dashboards.byId(p.dashboardId)!;
      // Place new widgets at the bottom of the existing grid — same logic
      // as the manual editor uses on add.
      const maxY = d.widgets.reduce(
        (m, w) => Math.max(m, w.layout.y + w.layout.h),
        0,
      );
      const widget: WidgetInstance = {
        id,
        type: p.widget.type,
        title: p.widget.title ?? entry.name,
        subtitle: p.widget.subtitle,
        layout:
          p.widget.layout ?? {
            x: 0,
            y: maxY,
            w: entry.defaultSize.w,
            h: entry.defaultSize.h,
          },
        dataSource: p.widget.dataSource,
        config: p.widget.config,
      };
      dashboards.addWidget(p.dashboardId, widget);
      return {
        ok: true,
        undoData: { dashboardId: p.dashboardId, widgetId: id },
        summary: `Added ${entry.name} widget to ${d.name}`,
      };
    },
  } as CopilotTool<AddWidgetPayload, { dashboardId: string; widgetId: string }>,

  bind_widget_slot: {
    id: "bind_widget_slot",
    description: "Bind one or more tag/port keys to a widget's named slot.",
    risk: "low",
    validate: (p, { dashboards }) => {
      const d = dashboards.byId(p.dashboardId);
      if (!d) return { ok: false, reason: `Dashboard "${p.dashboardId}" not found.` };
      if (!isEditable(d)) {
        return { ok: false, reason: `"${d.name}" is read-only.` };
      }
      const widget = d.widgets.find((w) => w.id === p.widgetId);
      if (!widget) {
        return { ok: false, reason: `Widget "${p.widgetId}" not found in ${d.name}.` };
      }
      const entry = WIDGET_REGISTRY[widget.type];
      const slot = entry?.accepts.find((s) => s.id === p.slotId);
      if (!slot) {
        return {
          ok: false,
          reason: `${entry?.name ?? widget.type} doesn't have a "${p.slotId}" slot.`,
        };
      }
      if (slot.required && p.keys.length === 0) {
        return { ok: false, reason: `${slot.label} is required.` };
      }
      if (!slot.multi && p.keys.length > 1) {
        return {
          ok: false,
          reason: `${slot.label} accepts only one key (got ${p.keys.length}).`,
        };
      }
      // Note: DataKind compatibility is checked at render time by
      // WidgetRenderer (InvalidBindingCard). We don't duplicate the
      // classifyKey logic here because it depends on the plant store
      // and the binding may still be valid against another alias.
      return { ok: true };
    },
    apply: (p, { dashboards }) => {
      const d = dashboards.byId(p.dashboardId)!;
      const widget = d.widgets.find((w) => w.id === p.widgetId)!;
      const previousDataSource = widget.dataSource;
      const nextSlots: Record<string, string[]> = {
        ...(previousDataSource?.slots ?? {}),
        [p.slotId]: p.keys,
      };
      const dataSource: DataSource = {
        kind: p.kind ?? previousDataSource?.kind ?? "tag",
        alias: p.alias,
        slots: nextSlots,
      };
      dashboards.setWidgetDataSource(p.dashboardId, p.widgetId, dataSource);
      return {
        ok: true,
        undoData: {
          dashboardId: p.dashboardId,
          widgetId: p.widgetId,
          previousDataSource,
        },
        summary: `Bound ${p.keys.length} key${p.keys.length === 1 ? "" : "s"} to ${
          widget.title ?? widget.type
        }.${p.slotId}`,
      };
    },
  } as CopilotTool<
    BindWidgetSlotPayload,
    { dashboardId: string; widgetId: string; previousDataSource?: DataSource }
  >,

  set_widget_title: {
    id: "set_widget_title",
    description: "Rename a widget's title and/or subtitle.",
    risk: "low",
    validate: (p, { dashboards }) => {
      const d = dashboards.byId(p.dashboardId);
      if (!d) return { ok: false, reason: `Dashboard "${p.dashboardId}" not found.` };
      if (!isEditable(d)) return { ok: false, reason: `"${d.name}" is read-only.` };
      if (!d.widgets.some((w) => w.id === p.widgetId)) {
        return { ok: false, reason: `Widget "${p.widgetId}" not found.` };
      }
      if (!p.title?.trim()) return { ok: false, reason: "Title cannot be empty." };
      return { ok: true };
    },
    apply: (p, { dashboards }) => {
      const d = dashboards.byId(p.dashboardId)!;
      const widget = d.widgets.find((w) => w.id === p.widgetId)!;
      const undoData = {
        dashboardId: p.dashboardId,
        widgetId: p.widgetId,
        previousTitle: widget.title,
        previousSubtitle: widget.subtitle,
      };
      dashboards.updateWidget(p.dashboardId, p.widgetId, {
        title: p.title,
        subtitle: p.subtitle,
      });
      return { ok: true, undoData, summary: `Renamed widget to "${p.title}"` };
    },
  } as CopilotTool<
    SetWidgetTitlePayload,
    { dashboardId: string; widgetId: string; previousTitle?: string; previousSubtitle?: string }
  >,

  delete_widget: {
    id: "delete_widget",
    description: "Remove a widget from a dashboard.",
    risk: "medium",
    validate: (p, { dashboards }) => {
      const d = dashboards.byId(p.dashboardId);
      if (!d) return { ok: false, reason: `Dashboard "${p.dashboardId}" not found.` };
      if (!isEditable(d)) return { ok: false, reason: `"${d.name}" is read-only.` };
      if (!d.widgets.some((w) => w.id === p.widgetId)) {
        return { ok: false, reason: `Widget "${p.widgetId}" not found.` };
      }
      return { ok: true };
    },
    apply: (p, { dashboards }) => {
      const d = dashboards.byId(p.dashboardId)!;
      const widget = d.widgets.find((w) => w.id === p.widgetId)!;
      dashboards.removeWidget(p.dashboardId, p.widgetId);
      return {
        ok: true,
        undoData: { dashboardId: p.dashboardId, widget },
        summary: `Deleted "${widget.title ?? widget.type}" from ${d.name}`,
      };
    },
  } as CopilotTool<DeleteWidgetPayload, { dashboardId: string; widget: WidgetInstance }>,

  create_alarm_rule: {
    id: "create_alarm_rule",
    description: "Create a new alarm rule on a tag.",
    risk: "low",
    validate: (p) => {
      if (!p.name?.trim()) return { ok: false, reason: "Rule name is required." };
      if (!p.targetTagId?.trim()) {
        return { ok: false, reason: "Target tag is required." };
      }
      const validOps = ["gt", "lt", "gte", "lte", "eq", "ne"];
      if (!validOps.includes(p.condition?.op)) {
        return {
          ok: false,
          reason: `Operator must be one of ${validOps.join(", ")} (got "${p.condition?.op}").`,
        };
      }
      if (p.severity && !["info", "warning", "critical"].includes(p.severity)) {
        return { ok: false, reason: `Invalid severity "${p.severity}".` };
      }
      return { ok: true };
    },
    apply: (p, { alarms }) => {
      const id = alarms.createRule({
        name: p.name.trim(),
        targetTagId: p.targetTagId,
        targetLabel: p.targetLabel,
        condition: p.condition,
        severity: p.severity ?? "warning",
        channels: p.channels ?? ["in-app"],
        enabled: p.enabled ?? true,
      });
      return { ok: true, undoData: { ruleId: id }, summary: `Created alarm rule "${p.name}"` };
    },
  } as CopilotTool<CreateAlarmRulePayload, { ruleId: string }>,

  update_alarm_rule: {
    id: "update_alarm_rule",
    description: "Patch an existing alarm rule.",
    risk: "low",
    validate: (p, { alarms }) => {
      const rule = alarms.ruleById(p.id);
      if (!rule) return { ok: false, reason: `Rule "${p.id}" not found.` };
      return { ok: true };
    },
    apply: (p, { alarms }) => {
      const previous = alarms.ruleById(p.id)!;
      alarms.updateRule(p.id, p.patch);
      return {
        ok: true,
        undoData: { ruleId: p.id, previous },
        summary: `Updated alarm rule "${previous.name}"`,
      };
    },
  } as CopilotTool<UpdateAlarmRulePayload, { ruleId: string; previous: AlarmRule }>,

  delete_alarm_rule: {
    id: "delete_alarm_rule",
    description: "Delete an alarm rule.",
    risk: "destructive",
    validate: (p, { alarms }) => {
      const rule = alarms.ruleById(p.id);
      if (!rule) return { ok: false, reason: `Rule "${p.id}" not found.` };
      return { ok: true };
    },
    apply: (p, { alarms }) => {
      const rule = alarms.ruleById(p.id)!;
      alarms.deleteRule(p.id);
      return { ok: true, undoData: { rule }, summary: `Deleted alarm rule "${rule.name}"` };
    },
  } as CopilotTool<DeleteAlarmRulePayload, { rule: AlarmRule }>,

  /**
   * patch_tag — kept as a stub in B0.43 because the tag catalog is
   * currently static (`FX_CATALOG_TAGS` in `lib/compute/__fixtures.ts`).
   * Once a `TagsProvider` exists in Phase 1 (or a sibling slice), this
   * tool will write through it. For now, validation always succeeds
   * and apply returns a no-op success so the proposal flow can still
   * be exercised end-to-end.
   */
  patch_tag: {
    id: "patch_tag",
    description: "Patch a tag's metadata (data_kind, unit, range, semantic tag).",
    risk: "low",
    validate: (p) => {
      if (!p.id) return { ok: false, reason: "Tag id is required." };
      return { ok: true };
    },
    apply: (p) => {
      // Phase 0 — accepted but a no-op against the static catalog.
      // Real implementation lands when a TagsProvider is introduced.
      return {
        ok: true,
        undoData: { id: p.id },
        summary: `Patched tag ${p.id} (Phase-0 stub — no catalog writes yet)`,
      };
    },
  } as CopilotTool<PatchTagPayload, { id: string; previous?: unknown }>,
} as const;

export type ToolId = keyof typeof COPILOT_TOOLS;

/** Dispatch helper: validate the payload, returning { ok, reason?, suggestion? }. */
export function validateTool(
  payload: CopilotToolPayload,
  stores: ToolStores,
): ValidationResult {
  const tool = COPILOT_TOOLS[payload.type as ToolId];
  if (!tool) {
    return { ok: false, reason: `Unknown tool "${payload.type}".` };
  }
  // The cast is safe because `payload.type` is the discriminator that
  // matches the tool's Payload type.
  return tool.validate(payload as never, stores);
}

/** Dispatch helper: apply the payload, returning { ok, reason?, undoData?, summary? }. */
export function applyTool(
  payload: CopilotToolPayload,
  stores: ToolStores,
): ApplyResult {
  const tool = COPILOT_TOOLS[payload.type as ToolId];
  if (!tool) {
    return { ok: false, reason: `Unknown tool "${payload.type}".` };
  }
  try {
    return tool.apply(payload as never, stores);
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : String(err),
    };
  }
}
